import { inject, Injectable } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { LottoDetails } from './models/lotto.model';
import { LottoItemStore } from './lotto-item.store';
import { BalanceService } from '../../../account/balance/balance.service';
import { config } from '../../../shared/config';
import { Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { LottoApiService } from './lotto-api.service';
import { TransactionSuccessStore } from '../../transaction-success/transaction-success.store';
import { CustomField } from '../../../shared/models/custom-field.model';
import { MarketplaceBasketCustomFieldModel } from '../../marketplace/models/basket/marketplace-basket-customfield.model';

/**
 * Orchestrates all side-effectful actions for the lotto domain.
 *
 * Separating actions from the store and service layer ensures that multiple consumers
 * (components, resolvers, other services) can trigger the same action without duplicating
 * logic or risking circular dependencies.
 *
 * Responsibilities:
 * - Coordinates API calls via LottoApiService
 * - Updates the LottoItemStore in response to API results
 * - Handles post-action side effects (balance debit, transaction success, navigation)
 */
@Injectable({
  providedIn: 'root',
})
export class LottoFacade {
  private store = inject(LottoItemStore);
  private transactionStore = inject(TransactionSuccessStore);
  private lottoService = inject(LottoApiService);
  private balanceService = inject(BalanceService);
  private router = inject(Router);

  /** Fetches all lottos from the API and replaces the store contents. */
  readonly fetchLottos = rxMethod<void>(
    pipe(
      switchMap(() =>
        this.lottoService.fetchLottos().pipe(
          tapResponse({
            next: (lottos: LottoDetails[]) => {
              this.store.set(lottos);
            },
            error: (error) => {},
          })
        )
      )
    )
  );

  /**
   * Fetches a single lotto by id and upserts it into the store.
   * Navigates to the 404 page if the item is not found or the request fails.
   */
  readonly fetchLottoById = rxMethod<number>(
    pipe(
      switchMap((id) =>
        this.lottoService.fetchLottoById(id).pipe(
          tapResponse({
            next: (lotto: LottoDetails) => {
              this.store.upsert(lotto);
            },
            error: (error) => {
              this.router.navigate(['/404page']);
            },
          })
        )
      )
    )
  );

  /**
   * Submits a lotto entry and handles all post-success side effects.
   *
   * On success:
   * - Optimistically updates the store's ticket count for the entered lotto
   * - Debits the user's virtual loyalty balance
   * - Sets the transaction success state for the confirmation screen
   * - Navigates to the success page
   */
  readonly enterLotto = rxMethod<{
    lotto: LottoDetails;
    entries: number;
    customFields?: MarketplaceBasketCustomFieldModel[];
    successUrl?: string;
  }>(
    pipe(
      tap(() => this.store.setLoading(true)),
      switchMap(({ lotto, entries, customFields, successUrl = '/rewards/raffles/purchase/success' }) => {
        const fields: Partial<CustomField>[] = customFields
          ? customFields.map((field: any) => ({ CustomFieldId: field.customFieldId, Value: field.customFieldValue }))
          : [];
        return this.lottoService.enterLotto(lotto.LottoId, entries, fields).pipe(
          tapResponse({
            next: (_) => {
              const cost = lotto.PurchasePrice * entries;
              this.store.updateById(lotto.LottoId, (state) => ({
                ...state,
                NumberOfTicketsPurchasedByAccount: (state?.NumberOfTicketsPurchasedByAccount ?? 0) + entries,
              }));
              this.balanceService.debitBalance(config.purseConfig.virtualLoyalty, cost);
              this.transactionStore.setTransaction(lotto as LottoDetails, entries, cost, customFields);
              this.router.navigateByUrl(successUrl);
            },
            error: (error) => {},
            finalize: () => this.store.setLoading(false),
          })
        );
      })
    )
  );

  /** Removes a lotto from the local store without making an API call. */
  removeById(id: number): void {
    this.store.removeEntity(id);
  }
}
